import { useState } from "react";
import api from "../api/PosApi";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Card, Col, Container, Row } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";

const MySwal = withReactContent(Swal);

export default function Ventas() {
  const [clientes, setClientes] = useState<any>([]);
  const [productos, setProductos] = useState<any>([]);
  const [clienteSel, setClienteSel] = useState<any>(null);
  const [productosSel, setProductosSel] = useState<any>([]);
  const [termCliente, setTermCliente] = useState("");
  const [termProd, setTermProd] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [tipoDte, setTipoDte] = useState("01");
  const [condicionOperacion, setCondicionOperacion] = useState(1);
  const [formaPago, setFormaPago] = useState("01");
  const [retieneRenta, setRetieneRenta] = useState(false);

  // Buscar clientes
  const buscarClientes = async () => {
    if (termCliente.trim().length < 2) return;
    const res = await api.get(`/pos/clientes?term=${termCliente}`);
    setClientes(res.data);
  };

  // Buscar productos
  const buscarProductos = async () => {
    if (termProd.trim().length < 2) return;
    const res = await api.get(`/pos/productos?term=${termProd}`);
    setProductos(res.data);
  };

  // Redondeo
  const redondear = (num: any) =>
    Math.round((num + Number.EPSILON) * 100) / 100;

  // Agregar producto al detalle
  const agregarProducto = (p: any) => {
    const existe = productosSel.find((x: any) => x.id === p.id);
    if (existe) return;

    const precioSinIva = redondear(p.precio / 1.13);

    setProductosSel([
      ...productosSel,
      {
        ...p,
        cantidad: 1,
        precioSinIva,
        totalSinIva: precioSinIva,
        totalConIva: p.precio,
      },
    ]);
    setProductos([]);
    setTermProd("");
  };

  // Actualizar cantidad y recalcular
  const actualizarCantidad = (id: any, valor: any) => {
    const nuevo = productosSel.map((p) => {
      if (p.id === id) {
        const cantidad = parseFloat(valor) || 0;
        const totalSinIva = redondear(p.precioSinIva * cantidad);
        const totalConIva = redondear(p.precio * cantidad);
        return { ...p, cantidad, totalSinIva, totalConIva };
      }
      return p;
    });
    setProductosSel(nuevo);
  };

  // Eliminar línea
  const eliminarLinea = (id: any) => {
    setProductosSel(productosSel.filter((p) => p.id !== id));
  };

  // Calcular totales (los precios están con IVA)
  const aplicaIVA = tipoDte !== "14"; // FSE no lleva IVA
  const subtotal = redondear(
    productosSel.reduce((s, p) => s + p.totalSinIva, 0)
  );
  const iva = aplicaIVA
    ? redondear(
        productosSel.reduce(
          (s, p) => s + (p.totalConIva - p.totalSinIva),
          0
        )
      )
    : 0;
  const totalPagar = redondear(subtotal + iva);

  // --- NUEVAS VARIABLES DE RETENCIONES ---
  const retencionIva =
    tipoDte === "03" && clienteSel?.ivaStatus === "retiene"
      ? redondear(subtotal * 0.01)
      : 0;

  const retencionRenta =
    tipoDte === "03" && retieneRenta ? redondear(subtotal * 0.1) : 0;

  const totalNeto = redondear(totalPagar - retencionIva - retencionRenta);

  // Emitir venta
  const emitirVenta = async () => {
    try {
      const confirm = await MySwal.fire({
        title: "¿Deseas emitir este documento?",
        text: "Esta acción guardará la información del documento a emitir, posteriormente deberás ir a 'Emisiones' para enviarlo a Hacienda.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, emitir",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "rgba(51, 221, 119, 1)",
        cancelButtonColor: "#3085d6",
        reverseButtons: true,
      });
      if (!confirm.isConfirmed) return;

      if (!clienteSel || productosSel.length === 0)
        return setMensaje("Selecciona cliente y al menos un producto");

      if(clienteSel.nrc=="" && clienteSel=="03"){
        MySwal.fire({
                icon: "warning",
                title: "Revisa los datos",
                text: `El cliente ingresado no tiene un nrc asociado, por lo que no se puede emitir un credito fiscal a ese cliente`,
                timer: 2500,
                showConfirmButton: false,
            });
        return;
      }

      const venta = {
        clienteId: String(clienteSel.id).toUpperCase(),
        tipoDte,
        condicionOperacion,
        formaPago,
        totalNoSuj: 0,
        totalExento: 0,
        totalGravado: totalPagar,
        totalIva: iva,
        totalPagar,
        ivaRet: retencionIva,      // <-- agregado
        reteRenta: retencionRenta, // <-- agregado
        documentoLineas: productosSel.map((p: any, i: number) => ({
          numItem: i + 1,
          descripcion: p.nombre,
          cantidad: p.cantidad,
          precioUnitario: p.precio,
          tipoItem: p.tipoItem,
          unidadMedida: String(p.unidadMedida).toUpperCase(),
          tributoCodigo: aplicaIVA ? "20" : null,
          totalLinea: p.totalConIva,
        })),
      };

      const doc = await api.post("/pos/ventas", venta);
      setMensaje(`✅ Emitida correctamente`);
      setProductosSel([]);
      setClienteSel(null);
    } catch (err) {
      console.error(err);
      setMensaje("❌ Error al emitir venta: " + err);
    }
  };

  return (
    <Container>
      <Row>
        <Col>
          <Card className="card-black">
            <Card.Header>
              <h3>Nueva Venta</h3>
              <p>
                A continuación podrás crear el documento de tu interés
                para realizar la venta de tu producto o servicio.
              </p>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col className="form-group">
                  <h5>Tipo de Documento:</h5>
                  <select
                    value={tipoDte}
                    onChange={(e) => setTipoDte(e.target.value)}
                    className="form-control"
                  >
                    <option value="01">Factura</option>
                    <option value="03">
                      Comprobante Crédito Fiscal
                    </option>
                    <option value="14">Factura Sujeto Excluido</option>
                  </select>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col>
                  <h5>Cliente:</h5>
                  <input
                    className="form-control"
                    placeholder="Buscar cliente..."
                    value={termCliente}
                    onChange={(e) => setTermCliente(e.target.value)}
                  />
                </Col>
                <Col xl={2}>
                  <button
                    onClick={buscarClientes}
                    className="btn btn-primary w-100 h-100"
                  >
                    Buscar
                  </button>
                </Col>
              </Row>
              <Row>
                <Col>
                  <ul className="list-group mt-2">
                    {clientes.map((c) => (
                      <li
                        className="list-group-item list-group-item-action list-group-item-success text-white mb-1"
                        key={c.id}
                        onClick={() => {
                          setClienteSel(c);
                          setClientes([]);
                          setTermCliente(c.nombre);
                        }}
                        style={{
                          cursor: "pointer",
                          background:
                            clienteSel?.id === c.id
                              ? "#e0f7fa"
                              : "transparent",
                        }}
                      >
                        {c.nombre} ({c.numeroDocumento}) -{" "}
                        {c.nrc == ""
                          ? "Cliente registrado únicamente para Factura CF"
                          : "Cliente con registro fiscal #" + c.nrc}
                      </li>
                    ))}
                  </ul>
                  {clienteSel && (
                    <div>
                      <p className="text-uppercase font-weight-bold text-success">
                        Seleccionado para{" "}
                        <strong>
                          {clienteSel.nrc == "" ? "FCF" : "CCF"}
                        </strong>
                        : {clienteSel.nombre}
                      </p>
                      <p>
                        <strong>¿Retiene IVA?:</strong>{" "}
                        {clienteSel.ivaStatus}
                      </p>
                      <p>
                        <strong>Tipo de contribuyente:</strong>{" "}
                        {clienteSel.tipoContribuyente}
                      </p>
                    </div>
                  )}
                  <div hidden>
                    ¿Retiene renta? 10%{" "}
                    <input
                      type="checkbox"
                      name="reteRenta"
                      id="reteRenta"
                      checked={retieneRenta}
                      onChange={(e) =>
                        setRetieneRenta(e.target.checked)
                      }
                    />
                  </div>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col className="form-group">
                  <h5>Productos / Servicios</h5>
                  <input
                    className="form-control"
                    placeholder="Buscar producto..."
                    value={termProd}
                    onChange={(e) => setTermProd(e.target.value)}
                  />
                </Col>
                <Col xl={2}>
                  <button
                    onClick={buscarProductos}
                    className="btn btn-primary w-100 h-100"
                  >
                    Buscar
                  </button>
                </Col>
              </Row>
              <Row>
                <Col>
                  <ul className="list-group mt-2">
                    {productos.map((p) => (
                      <li
                        className="list-group-item list-group-item-action list-group-item-dark mb-1"
                        key={p.id}
                        onClick={() => agregarProducto(p)}
                        style={{ cursor: "pointer" }}
                      >
                        {p.nombre} - ${p.precio.toFixed(2)} (precio con IVA)
                      </li>
                    ))}
                  </ul>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col>
                  <h2 className="text-center">Detalle de Venta</h2>
                </Col>
              </Row>
              <Row>
                <Col>
                  {productosSel.length === 0 ? (
                    <p className="text-secondary text-center py-3">
                      Sin productos agregados
                    </p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-dark table-striped align-middle text-center">
                        <thead>
                          <tr>
                            <th>Producto</th>
                            <th style={{ width: "100px" }}>Cant.</th>
                            <th>Precio sin IVA</th>
                            <th>Precio con IVA</th>
                            <th>Total sin IVA</th>
                            <th>Total con IVA</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {productosSel.map((p: any) => (
                            <tr key={p.id}>
                              <td className="text-start">{p.nombre}</td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={p.cantidad}
                                  onChange={(e) =>
                                    actualizarCantidad(
                                      p.id,
                                      parseFloat(e.target.value)
                                    )
                                  }
                                  className="form-control bg-dark text-light border-secondary text-center"
                                  style={{
                                    width: "80px",
                                    margin: "auto",
                                  }}
                                />
                              </td>
                              <td>${p.precioSinIva.toFixed(2)}</td>
                              <td>${p.precio.toFixed(2)}</td>
                              <td>${p.totalSinIva.toFixed(2)}</td>
                              <td>${p.totalConIva.toFixed(2)}</td>
                              <td>
                                <button
                                  onClick={() => eliminarLinea(p.id)}
                                  className="btn btn-sm btn-outline-danger"
                                >
                                  <i className="bi bi-x-lg"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Col>
              </Row>
              <hr />

              {/* Totales */}
              <Row>
                <Col className="d-flex flex-row-reverse text-uppercase">
                  <p>
                    Subtotal (sin IVA):{" "}
                    <strong>${subtotal.toFixed(2)}</strong>
                  </p>
                </Col>
              </Row>

              <Row>
                <Col className="d-flex flex-row-reverse text-uppercase">
                  <p>
                    IVA (13%): <strong>${iva.toFixed(2)}</strong>
                  </p>
                </Col>
              </Row>

              {retencionIva > 0 && (
                <Row>
                  <Col className="d-flex flex-row-reverse text-uppercase">
                    <p>
                      IVA RET (1%):{" "}
                      <strong>${retencionIva.toFixed(2)}</strong>
                    </p>
                  </Col>
                </Row>
              )}

              {retencionRenta > 0 && (
                <Row>
                  <Col className="d-flex flex-row-reverse text-uppercase">
                    <p>
                      Renta RET (10%):{" "}
                      <strong>${retencionRenta.toFixed(2)}</strong>
                    </p>
                  </Col>
                </Row>
              )}

              <Row>
                <Col className="d-flex flex-row-reverse text-uppercase">
                  <h3>
                    Total a Pagar:{" "}
                    <strong>${totalPagar.toFixed(2)}</strong>
                  </h3>
                </Col>
              </Row>

              {(retencionIva > 0 || retencionRenta > 0) && (
                <Row>
                  <Col className="d-flex flex-row-reverse text-uppercase">
                    <h4>
                      Total Neto Recibir:{" "}
                      <strong>${totalNeto.toFixed(2)}</strong>
                    </h4>
                  </Col>
                </Row>
              )}

              <Row>
                <Col>
                  <p className="text-dark">{mensaje && mensaje}</p>
                </Col>
              </Row>
              <Row>
                <Col>
                  <button
                    onClick={emitirVenta}
                    id="action"
                    className="btn btn-success w-100"
                    disabled={!clienteSel || productosSel.length === 0}
                  >
                    Emitir Venta
                  </button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
